import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { ZoomInIcon, ZoomOutIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';
import type { EnrichedReadyToSharePlatformPostV2 } from 'src/app/shared/api/readyToShare';

import { PostPreview } from '../../../PostPreview';

import { usePostPreviewContainer } from './hooks/usePostPreviewContainer';

export type PostPreviewContainerProps = {
	variant: EnrichedReadyToSharePlatformPostV2['__typename'];
	message?: string;
	previewImageUrl?: string;
	recommendedGroup?: RecommendedGroup;
	showDisclaimer?: boolean;
	petition?: StarterDashboardV2PageData['petition'];
	isLoading: boolean;
};

export const PostPreviewContainer: React.FC<PostPreviewContainerProps> = (props) => {
	const { translate } = useI18n();
	const {
		data: { isMobile, isExpanded },
		actions: { toggleExpanded },
	} = usePostPreviewContainer();

	const { message, previewImageUrl, variant, recommendedGroup, showDisclaimer, petition, isLoading } = props;

	const sharingResource = {
		message: message || '',
		previewImageUrl: previewImageUrl || '',
		recommendedGroup,
	};

	return (
		<section className="rounded-large bg-background-overlay">
			{!isLoading && (
				<div className="relative">
					<div className="absolute top-4 right-4">
						{/* Expand feature is only available in mobile */}
						{isMobile && (
							<Button
								data-qa="expand-post-preview-button"
								data-testid="expand-post-preview-button"
								aria-label={translate(
									'corgi.petition.starter_dashboard_v2.shared.sharing_modal.post_preview_container.expand_preview_aria_label',
								)}
								variant="secondary-solid"
								size="x-small"
								onClick={toggleExpanded}
								leadingIcon={
									isExpanded ? (
										<ZoomInIcon data-testid="zoom-in-icon" size="small" />
									) : (
										<ZoomOutIcon data-testid="zoom-out-icon" size="small" />
									)
								}
								iconOnly
							/>
						)}
					</div>
				</div>
			)}
			<div className="flex flex-col items-center gap-3 px-12 py-4 md:px-8">
				<PostPreview
					variant={variant}
					size={isExpanded ? 'medium' : 'small'}
					showPlatformIcon={false}
					loading={isLoading}
					sharingResource={sharingResource}
					petition={petition}
				/>
				{showDisclaimer && (
					<Text data-testid="disclaimer-text" className="text-size-body-small">
						{isLoading
							? translate(
									'corgi.petition.starter_dashboard_v2.shared.sharing_modal.post_preview_container.disclaimer_loading',
								)
							: translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.post_preview_container.disclaimer')}
					</Text>
				)}
			</div>
		</section>
	);
};
